// src/app/theme/ThemeEngineProvider.tsx
import { createContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import type { ReactNode } from 'react';
import {
  ThemeEngine,
  registerThemes,
  type ITheme,
  type IThemeRegistryEntry,
} from '@vanialabs/ui-engine';

import { applyThemeToDom } from './applyThemeToDom';
import { localThemePreferenceStore } from './localThemePreferenceStore';

export interface IThemeEngineContextValue {
  /** Currently active, fully resolved theme — null until initialize() resolves. */
  theme: ITheme | null;
  /** Activates a theme by id and persists the selection. */
  setTheme: (id: string) => Promise<void>;
  /** Registered themes' metadata (no theme code loaded by reading this). */
  availableThemes: ReturnType<ThemeEngine['listAvailableThemes']>;
}

export const ThemeEngineContext = createContext<IThemeEngineContextValue | null>(null);

/**
 * One ThemeEngine instance for the whole app, constructed at module
 * scope so it exists exactly once regardless of how many times
 * ThemeEngineProvider re-renders. `registerThemes` (from ui-engine) is
 * the single place that knows the concrete theme list — Core code,
 * including this file, never imports a theme module directly.
 */
const themeEngine = new ThemeEngine({
  defaultThemeId: 'default',
  preferenceStore: localThemePreferenceStore,
});
registerThemes(themeEngine);

/**
 * Phase 4.7 — exposes the single module-scoped ThemeEngine instance
 * above so app-level composition (App.tsx) can pass it into
 * <TenantThemeProvider> without constructing a second instance.
 * Purely additive: no change to provider behavior.
 */
export { themeEngine };

/**
 * Tracks whether `themeEngine.initialize()` has already resolved once
 * during this page load. Module-scoped (not component state) so that
 * if `ThemeEngineProvider` ever remounts within the same session --
 * React Strict Mode's dev double-mount, a route/layout remount, HMR --
 * we don't re-show the hydration guard and blank the screen a second
 * time for a theme that's already known.
 */
let hasResolvedInitialTheme = false;

export function ThemeEngineProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ITheme | null>(themeEngine.getActiveTheme());
  const [availableThemes, setAvailableThemes] = useState<IThemeRegistryEntry[]>(themeEngine.listAvailableThemes());
  // Gate on the *persisted* theme having actually been resolved, not
  // just on `theme` being non-null -- `getActiveTheme()` above can
  // already be non-null (e.g. the configured default) before
  // `initialize()` has had a chance to load and apply the user's
  // saved preference. Rendering `children` on that early non-null
  // value is exactly what produces the default-theme flash.
  const [isReady, setIsReady] = useState(hasResolvedInitialTheme);

  useEffect(() => {
    let cancelled = false;

    // Subscribe first so no state change during initialize() is missed.
    const unsubscribe = themeEngine.subscribe((state) => {
      // Paint the new theme's CSS variables onto the DOM first, and
      // only then commit the React state update -- synchronously, via
      // flushSync, so React never gets a chance to schedule a repaint
      // between "DOM has new theme" and "React knows about new theme".
      // This is what makes the switch atomic: there is no frame in
      // which the visible CSS variables still belong to the previous
      // theme.
      if (state.activeTheme) {
        applyThemeToDom(state.activeTheme);
      }
      flushSync(() => {
        setThemeState(state.activeTheme);
      });
    });

    void themeEngine
      .initialize()
      .then(() => {
        if (cancelled) return;
        const resolved = themeEngine.getActiveTheme();
        // Apply the resolved (persisted) theme's DOM variables before
        // the state update that unblocks `children` below, and flush
        // that state update synchronously -- so by the time `children`
        // gets its first real paint, the DOM already carries the
        // correct, persisted theme rather than the default.
        if (resolved) {
          applyThemeToDom(resolved);
        }
        flushSync(() => {
          setThemeState(resolved);
          setAvailableThemes(themeEngine.listAvailableThemes());
          setIsReady(true);
        });
        hasResolvedInitialTheme = true;
      })
      .catch(() => {
        // Resolution failed for some reason -- don't leave the app
        // stuck behind a blank screen forever. Fall back to whatever
        // theme the engine already has synchronously (its configured
        // default) and let rendering proceed.
        if (cancelled) return;
        const fallback = themeEngine.getActiveTheme();
        if (fallback) {
          applyThemeToDom(fallback);
        }
        flushSync(() => {
          setThemeState(fallback);
          setIsReady(true);
        });
        hasResolvedInitialTheme = true;
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const setTheme = useMemo(
    () => async (id: string) => {
      await themeEngine.activateTheme(id);
    },
    [],
  );

  const value: IThemeEngineContextValue = { theme, setTheme, availableThemes };

  // Hold rendering until the persisted theme is resolved and applied,
  // instead of letting `children` mount against whatever the engine's
  // pre-initialize default happens to be. Keep a transparent shell-sized
  // node in the DOM during this short boot window so visual QA and the
  // browser never observe an empty document viewport.
  if (!isReady) {
    return <div className="vania-theme-initializing" aria-hidden="true" />;
  }

  return <ThemeEngineContext.Provider value={value}>{children}</ThemeEngineContext.Provider>;
}
